import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Separator } from '../../ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Progress } from '../../ui/progress';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { 
  CreditCard, 
  Plus,
  Search,
  Eye,
  Download,
  TrendingUp,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Users,
  Building,
  Save,
  X,
  Edit,
  Upload,
  Trash2,
  Copy,
  Archive,
  MoreVertical,
  Paperclip,
  RefreshCw,
  FileText,
  Clock,
  Send
} from 'lucide-react';
import { financialService, PaymentApp, PaymentAppCreateData } from '../../../src/services/financial.service';
import { useAuth } from '../../../src/hooks/useAuth';
import { usePaymentApps } from '../../../src/hooks/queries/use-payment-apps';
import { useContracts } from '../../../src/hooks/queries/use-contracts';
import { useAppNav } from '../../../src/contexts/app-nav.context';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { useOrgCurrency } from '../../../src/hooks/useOrgCurrency';
import { formatCurrencyCompact } from '../../../src/utils/currency';

// Payment Application Form Component
function PaymentApplicationFormModal({ mode, application, trigger, onSuccess, orgId }: {
  mode: 'add' | 'edit' | 'view';
  application?: any;
  trigger: React.ReactNode;
  onSuccess?: () => void;
  orgId?: string;
}) {
  const isReadOnly = mode === 'view';
  const orgCurrency = useOrgCurrency();
  const [activeTab, setActiveTab] = useState('basic');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    applicationNumber: application?.applicationNumber || '',
    project: application?.project || '',
    contract: application?.contract || '',
    client: application?.client || '',
    billingPeriod: application?.billingPeriod || '',
    periodStartDate: application?.periodStartDate || '',
    periodEndDate: application?.periodEndDate || '',
    submissionDate: application?.submissionDate || new Date().toISOString().split('T')[0],
    dueDate: application?.dueDate || '',
    description: application?.description || '',
    retentionPercentage: application?.retentionPercentage || '10',
    previouslyBilled: application?.previouslyBilled || '0',
    currentPeriodWork: application?.currentPeriodWork || '0',
    totalToDate: application?.totalToDate || '0',
    retentionAmount: application?.retentionAmount || '0',
    amountDue: application?.amountDue || '0',
    changeOrderAmount: application?.changeOrderAmount || '0',
    materialOnSite: application?.materialOnSite || '0',
    notes: application?.notes || '',
    workItems: application?.workItems || [
      { id: 1, description: 'Site Preparation', scheduledValue: 50000, previouslyBilled: 50000, currentWork: 0, totalCompleted: 50000, percentComplete: 100, balance: 0 },
      { id: 2, description: 'Foundation Work', scheduledValue: 120000, previouslyBilled: 80000, currentWork: 40000, totalCompleted: 120000, percentComplete: 100, balance: 0 },
      { id: 3, description: 'Structural Steel', scheduledValue: 350000, previouslyBilled: 200000, currentWork: 100000, totalCompleted: 300000, percentComplete: 86, balance: 50000 },
      { id: 4, description: 'MEP Rough-in', scheduledValue: 180000, previouslyBilled: 0, currentWork: 45000, totalCompleted: 45000, percentComplete: 25, balance: 135000 }
    ],
    changeOrders: application?.changeOrders || [
      { id: 1, coNumber: 'CO-2024-001', description: 'Additional Electrical Outlets', originalAmount: 4500, approvedAmount: 4500, previouslyBilled: 0, currentBilling: 4500 },
      { id: 2, coNumber: 'CO-2024-002', description: 'Foundation Design Revision', originalAmount: 12000, approvedAmount: 12000, previouslyBilled: 12000, currentBilling: 0 }
    ],
    reviewHistory: application?.reviewHistory || [
      { id: 1, reviewer: 'Project Manager', role: 'PM', status: 'Pending', date: '', comments: '', level: 1, required: true },
      { id: 2, reviewer: 'Client Representative', role: 'Client', status: 'Pending', date: '', comments: '', level: 2, required: true },
      { id: 3, reviewer: 'Finance Manager', role: 'Finance', status: 'Pending', date: '', comments: '', level: 3, required: true }
    ],
    attachments: application?.attachments || [
      { id: 1, name: 'Payment_Application_G702.pdf', type: 'Application', size: '2.8 MB', uploadDate: '2024-08-15', uploadedBy: 'Project Manager' },
      { id: 2, name: 'Schedule_of_Values.xlsx', type: 'Schedule', size: '1.1 MB', uploadDate: '2024-08-15', uploadedBy: 'Project Manager' },
      { id: 3, name: 'Continuation_Sheet_G703.pdf', type: 'Continuation', size: '1.5 MB', uploadDate: '2024-08-15', uploadedBy: 'Project Manager' },
      { id: 4, name: 'Photos_Progress.zip', type: 'Photos', size: '15.2 MB', uploadDate: '2024-08-15', uploadedBy: 'Site Supervisor' }
    ]
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const projects = [
    'Residential Complex Alpha',
    'Office Building Beta',
    'Industrial Warehouse Gamma',
    'Shopping Center Delta',
    'Mixed-Use Development Epsilon'
  ];

  const contracts = [
    'CNT-2024-001 - Main Construction Contract',
    'CNT-2024-002 - MEP Systems Installation',
    'CNT-2024-003 - Structural Steel Fabrication',
    'CNT-2024-004 - Site Preparation & Excavation',
    'CNT-2024-005 - Architectural Design Services'
  ];

  const clients = [
    'ABC Development Corp',
    'Metro Business Park',
    'LogiCorp Industries',
    'Retail Developments LLC',
    'Urban Spaces Development'
  ];

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.applicationNumber.trim()) {
      newErrors.applicationNumber = 'Application number is required';
    }
    if (!formData.project) {
      newErrors.project = 'Project is required';
    }
    if (!formData.client) {
      newErrors.client = 'Client is required';
    }
    if (!formData.periodStartDate) {
      newErrors.periodStartDate = 'Period start date is required';
    }
    if (!formData.periodEndDate) {
      newErrors.periodEndDate = 'Period end date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const lineItems = formData.workItems.map((item: any, idx: number) => ({
        description: item.description,
        scheduled_value: item.scheduledValue || 0,
        work_completed_from_previous: item.previouslyBilled || 0,
        work_completed_this_period: item.currentWork || 0,
        materials_stored: 0,
        total_completed_and_stored: item.totalCompleted || 0,
        percentage_complete: item.percentComplete || 0,
        balance_to_finish: item.balance || 0,
        retention_amount: 0,
        sort_order: idx,
      }));
      const totalWork = formData.workItems.reduce((s: number, i: any) => s + (i.currentWork || 0), 0);
      const retPct = parseFloat(formData.retentionPercentage) || 10;
      const retAmt = totalWork * (retPct / 100);
      const payload: PaymentAppCreateData = {
        organization_id: orgId || '',
        project_id: formData.project || undefined,
        contract_id: formData.contract || undefined,
        client_name: formData.client || undefined,
        application_number: formData.applicationNumber,
        application_date: formData.submissionDate,
        billing_period_start: formData.periodStartDate || undefined,
        billing_period_end: formData.periodEndDate || undefined,
        work_completed_amount: totalWork,
        materials_stored_amount: parseFloat(formData.materialOnSite) || 0,
        change_order_amount: parseFloat(formData.changeOrderAmount) || 0,
        total_application_amount: totalWork + (parseFloat(formData.changeOrderAmount) || 0),
        retention_percentage: retPct,
        retention_amount: retAmt,
        net_payment_due: totalWork - retAmt,
        currency: orgCurrency,
        description: formData.description || undefined,
        notes: formData.notes || undefined,
        status: 'Draft',
        line_items: lineItems,
      };
      if (application?.application_id) {
        await financialService.updatePaymentApp(application.application_id, payload);
        showSuccess('Payment application updated');
      } else {
        await financialService.createPaymentApp(payload);
        showSuccess('Payment application created');
      }
      onSuccess?.();
    } catch {
      showError('Failed to save payment application');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateWorkItem = (id: number, field: string, value: string | number) => {
    setFormData({
      ...formData,
      workItems: formData.workItems.map(item => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          // Recalculate dependent fields
          if (field === 'currentWork' || field === 'previouslyBilled') {
            updated.totalCompleted = updated.previouslyBilled + updated.currentWork;
            updated.percentComplete = Math.min(100, (updated.totalCompleted / updated.scheduledValue) * 100);
            updated.balance = updated.scheduledValue - updated.totalCompleted;
          }
          return updated;
        }
        return item;
      })
    });
  };

  const updateChangeOrder = (id: number, field: string, value: string | number) => {
    setFormData({
      ...formData,
      changeOrders: formData.changeOrders.map(co => 
        co.id === id ? { ...co, [field]: value } : co
      )
    });
  };

  const updateReviewHistory = (id: number, field: string, value: string | boolean) => {
    setFormData({
      ...formData,
      reviewHistory: formData.reviewHistory.map(review => 
        review.id === id ? { ...review, [field]: value } : review
      )
    });
  };

  const calculateTotals = () => {
    const currentWork = formData.workItems.reduce((sum, item) => sum + item.currentWork, 0);
    const changeOrderCurrent = formData.changeOrders.reduce((sum, co) => sum + co.currentBilling, 0);
    const totalCurrent = currentWork + changeOrderCurrent;
    const retentionAmount = totalCurrent * (parseFloat(formData.retentionPercentage) / 100);
    const amountDue = totalCurrent - retentionAmount;
    
    return { currentWork, changeOrderCurrent, totalCurrent, retentionAmount, amountDue };
  };

  const { currentWork, changeOrderCurrent, totalCurrent, retentionAmount, amountDue } = calculateTotals();

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[1400px] max-h-[95vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Application (AR) - {mode === 'add' ? 'New' : mode === 'edit' ? 'Edit' : 'View'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'add' ? 'Create a new payment application for work completed during billing period' : 
             mode === 'edit' ? 'Update payment application information and work progress' : 
             'View detailed payment application and approval status'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="workitems">Schedule of Values</TabsTrigger>
            <TabsTrigger value="changeorders">Change Orders</TabsTrigger>
            <TabsTrigger value="review">Review Process</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Application Information
                </h3>
                
                <div className="space-y-2">
                  <Label htmlFor="application-number">Application Number *</Label>
                  <Input 
                    id="application-number" 
                    value={formData.applicationNumber}
                    onChange={(e) => setFormData({...formData, applicationNumber: e.target.value})}
                    placeholder="Enter application number"
                    readOnly={isReadOnly}
                  />
                  {errors.applicationNumber && <p className="text-sm text-destructive">{errors.applicationNumber}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="project">Project *</Label>
                  <Select value={formData.project} onValueChange={(value) => setFormData({...formData, project: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map(project => (
                        <SelectItem key={project} value={project}>{project}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.project && <p className="text-sm text-destructive">{errors.project}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contract">Contract</Label>
                  <Select value={formData.contract} onValueChange={(value) => setFormData({...formData, contract: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select contract" />
                    </SelectTrigger>
                    <SelectContent>
                      {contracts.map(contract => (
                        <SelectItem key={contract} value={contract}>{contract}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client">Client *</Label>
                  <Select value={formData.client} onValueChange={(value) => setFormData({...formData, client: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client} value={client}>{client}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.client && <p className="text-sm text-destructive">{errors.client}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="billing-period">Billing Period</Label>
                  <Input 
                    id="billing-period" 
                    value={formData.billingPeriod}
                    onChange={(e) => setFormData({...formData, billingPeriod: e.target.value})}
                    placeholder="e.g., Month 6, Q2 2024"
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea 
                    id="description" 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Enter application description"
                    rows={3}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Dates & Summary
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="period-start">Period Start Date *</Label>
                    <Input 
                      id="period-start" 
                      type="date"
                      value={formData.periodStartDate}
                      onChange={(e) => setFormData({...formData, periodStartDate: e.target.value})}
                      readOnly={isReadOnly}
                    />
                    {errors.periodStartDate && <p className="text-sm text-destructive">{errors.periodStartDate}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="period-end">Period End Date *</Label>
                    <Input 
                      id="period-end" 
                      type="date"
                      value={formData.periodEndDate}
                      onChange={(e) => setFormData({...formData, periodEndDate: e.target.value})}
                      readOnly={isReadOnly}
                    />
                    {errors.periodEndDate && <p className="text-sm text-destructive">{errors.periodEndDate}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="submission-date">Submission Date</Label>
                    <Input 
                      id="submission-date" 
                      type="date"
                      value={formData.submissionDate}
                      onChange={(e) => setFormData({...formData, submissionDate: e.target.value})}
                      readOnly={isReadOnly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="due-date">Payment Due Date</Label>
                    <Input 
                      id="due-date" 
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                      readOnly={isReadOnly}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="retention">Retention Percentage (%)</Label>
                  <Input 
                    id="retention" 
                    type="number"
                    value={formData.retentionPercentage}
                    onChange={(e) => setFormData({...formData, retentionPercentage: e.target.value})}
                    placeholder="10"
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-semibold mb-2">Payment Summary</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Current Period Work:</span>
                      <span>${currentWork.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Change Orders:</span>
                      <span>${changeOrderCurrent.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Current:</span>
                      <span>${totalCurrent.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Retention ({formData.retentionPercentage}%):</span>
                      <span>-${retentionAmount.toLocaleString()}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between font-semibold">
                      <span>Amount Due:</span>
                      <span>${amountDue.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea 
                    id="notes" 
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Additional notes or comments"
                    rows={3}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="workitems" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Schedule of Values (G702/G703)
              </h3>
            </div>
            
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead>Scheduled Value</TableHead>
                    <TableHead>Previously Billed</TableHead>
                    <TableHead>Current Work</TableHead>
                    <TableHead>Total Completed</TableHead>
                    <TableHead>% Complete</TableHead>
                    <TableHead>Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.workItems.map(item => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.description}</TableCell>
                      <TableCell>${item.scheduledValue.toLocaleString()}</TableCell>
                      <TableCell>${item.previouslyBilled.toLocaleString()}</TableCell>
                      <TableCell>
                        <Input 
                          type="number"
                          value={item.currentWork}
                          onChange={(e) => updateWorkItem(item.id, 'currentWork', parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          readOnly={isReadOnly}
                          className="w-24"
                        />
                      </TableCell>
                      <TableCell>${item.totalCompleted.toLocaleString()}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{item.percentComplete.toFixed(1)}%</span>
                          <Progress value={item.percentComplete} className="h-2 w-16" />
                        </div>
                      </TableCell>
                      <TableCell>${item.balance.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="changeorders" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Change Orders
              </h3>
            </div>
            
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>CO Number</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Original Amount</TableHead>
                    <TableHead>Approved Amount</TableHead>
                    <TableHead>Previously Billed</TableHead>
                    <TableHead>Current Billing</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.changeOrders.map(co => (
                    <TableRow key={co.id}>
                      <TableCell className="font-medium">{co.coNumber}</TableCell>
                      <TableCell>{co.description}</TableCell>
                      <TableCell>${co.originalAmount.toLocaleString()}</TableCell>
                      <TableCell>${co.approvedAmount.toLocaleString()}</TableCell>
                      <TableCell>${co.previouslyBilled.toLocaleString()}</TableCell>
                      <TableCell>
                        <Input 
                          type="number"
                          value={co.currentBilling}
                          onChange={(e) => updateChangeOrder(co.id, 'currentBilling', parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          readOnly={isReadOnly}
                          className="w-24"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {formData.changeOrders.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No change orders for this application</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="review" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Review & Approval Process
              </h3>
            </div>
            
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Level</TableHead>
                    <TableHead>Reviewer</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Required</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Comments</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.reviewHistory.map(review => (
                    <TableRow key={review.id}>
                      <TableCell>
                        <Badge variant="outline">Level {review.level}</Badge>
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={review.reviewer}
                          onChange={(e) => updateReviewHistory(review.id, 'reviewer', e.target.value)}
                          placeholder="Reviewer name"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={review.role}
                          onChange={(e) => updateReviewHistory(review.id, 'role', e.target.value)}
                          placeholder="Role"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={review.required.toString()} 
                          onValueChange={(value) => updateReviewHistory(review.id, 'required', value === 'true')}
                          disabled={isReadOnly}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true">Required</SelectItem>
                            <SelectItem value="false">Optional</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={review.status} 
                          onValueChange={(value) => updateReviewHistory(review.id, 'status', value)}
                          disabled={isReadOnly}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pending">Pending</SelectItem>
                            <SelectItem value="Approved">Approved</SelectItem>
                            <SelectItem value="Rejected">Rejected</SelectItem>
                            <SelectItem value="Under Review">Under Review</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="date"
                          value={review.date}
                          onChange={(e) => updateReviewHistory(review.id, 'date', e.target.value)}
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={review.comments}
                          onChange={(e) => updateReviewHistory(review.id, 'comments', e.target.value)}
                          placeholder="Comments"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Paperclip className="h-5 w-5" />
                Supporting Documents
              </h3>
              {!isReadOnly && (
                <Button size="sm">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              )}
            </div>
            
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Upload Date</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.attachments.map(doc => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-medium">{doc.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{doc.type}</Badge>
                      </TableCell>
                      <TableCell>{doc.size}</TableCell>
                      <TableCell>{doc.uploadDate}</TableCell>
                      <TableCell>{doc.uploadedBy}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                          {!isReadOnly && (
                            <Button variant="outline" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button variant="outline">
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          {!isReadOnly && (
            <>
              <Button variant="outline">
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </Button>
              <Button onClick={handleSave} disabled={isSubmitting}>
                {isSubmitting ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {mode === 'add' ? 'Submit Application' : 'Update Application'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PaymentApplicationsAR() {
  const { orgId } = useAuth();
  const orgCurrency = useOrgCurrency();
  const { routeFilters } = useAppNav();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [contractFilter, setContractFilter] = useState('all');
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [approveAmount, setApproveAmount] = useState('');

  const { data: rawApps = [], isLoading: loading, refetch } = usePaymentApps(orgId);
  const { data: rawContracts = [] } = useContracts(orgId);
  const contracts = rawContracts as any[];

  // Apply incoming route filters on first render
  useEffect(() => {
    if (routeFilters.contract_id) setContractFilter(routeFilters.contract_id);
  }, []); // intentional empty dep

  const getContractName = (contractId: string) => {
    const c = contracts.find((c: any) => c.contract_id === contractId);
    return c ? (c.contract_name || c.contract_number || contractId) : contractId;
  };

  const handleSubmitApp = async (id: string) => {
    try {
      await financialService.submitPaymentApp(id);
      showSuccess('Payment application submitted');
      refetch();
    } catch {
      showError('Failed to submit payment application');
    }
  };

  const openApproveDialog = (id: string, requestedAmount?: number) => {
    setApprovingId(id);
    setApproveAmount(requestedAmount !== undefined ? String(requestedAmount) : '');
    setApproveDialogOpen(true);
  };

  const doApproveApp = async () => {
    if (!approvingId) return;
    const amount = parseFloat(approveAmount);
    if (isNaN(amount) || amount < 0) return;
    try {
      await financialService.approvePaymentApp(approvingId, amount);
      showSuccess('Payment application approved');
      setApproveDialogOpen(false);
      refetch();
    } catch {
      showError('Failed to approve payment application');
    }
  };

  const handleDeleteApp = async (id: string) => {
    try {
      await financialService.deletePaymentApp(id);
      showSuccess('Payment application deleted');
      refetch();
    } catch {
      showError('Failed to delete payment application');
    }
  };

  const applications = (rawApps as PaymentApp[]).map(a => ({
    id: a.application_id,
    applicationNumber: a.application_number,
    project: a.project_id || '',
    contract: a.contract_id || '',
    client: a.client_name || '',
    billingPeriod: a.billing_period_start ? `${a.billing_period_start} – ${a.billing_period_end}` : '',
    periodStartDate: a.billing_period_start || '',
    periodEndDate: a.billing_period_end || '',
    submissionDate: a.application_date,
    dueDate: '',
    currentAmount: a.total_application_amount,
    retentionAmount: a.retention_amount,
    amountDue: a.net_payment_due,
    status: a.status,
    reviewLevel: 1,
    percentComplete: 0,
    description: a.description || '',
    documentsCount: 0,
    lastUpdated: a.updated_at,
    _raw: a,
  }));
  // mock data removed
  void [
    {
      id: 1,
      applicationNumber: "PLACEHOLDER",
      project: "Residential Complex Alpha",
      contract: "CNT-2024-001 - Main Construction Contract",
      client: "ABC Development Corp",
      billingPeriod: "Month 6",
      periodStartDate: "2024-08-01",
      periodEndDate: "2024-08-31",
      submissionDate: "2024-09-02",
      dueDate: "2024-09-17",
      currentAmount: 125000,
      retentionAmount: 12500,
      amountDue: 112500,
      status: "Under Review",
      reviewLevel: 2,
      percentComplete: 65,
      description: "Monthly payment application for August 2024 work",
      documentsCount: 4,
      lastUpdated: "2024-09-02"
    },
    {
      id: 2,
      applicationNumber: "PAY-2024-002",
      project: "Office Building Beta",
      contract: "CNT-2024-002 - MEP Systems Installation",
      client: "Metro Business Park",
      billingPeriod: "Month 4",
      periodStartDate: "2024-08-01",
      periodEndDate: "2024-08-31",
      submissionDate: "2024-09-01",
      dueDate: "2024-09-16",
      currentAmount: 87500,
      retentionAmount: 8750,
      amountDue: 78750,
      status: "Approved",
      reviewLevel: 3,
      percentComplete: 45,
      description: "MEP installation progress billing",
      documentsCount: 6,
      lastUpdated: "2024-09-05"
    },
    {
      id: 3,
      applicationNumber: "PAY-2024-003",
      project: "Industrial Warehouse Gamma",
      contract: "CNT-2024-003 - Structural Steel Fabrication",
      client: "LogiCorp Industries",
      billingPeriod: "Month 8",
      periodStartDate: "2024-08-01",
      periodEndDate: "2024-08-31",
      submissionDate: "2024-08-30",
      dueDate: "2024-09-14",
      currentAmount: 156000,
      retentionAmount: 15600,
      amountDue: 140400,
      status: "Paid",
      reviewLevel: 3,
      percentComplete: 80,
      description: "Structural steel completion milestone",
      documentsCount: 5,
      lastUpdated: "2024-09-10"
    },
    {
      id: 4,
      applicationNumber: "PAY-2024-004",
      project: "Shopping Center Delta",
      contract: "CNT-2024-006 - Roofing & Waterproofing",
      client: "Retail Developments LLC",
      billingPeriod: "Month 2",
      periodStartDate: "2024-07-15",
      periodEndDate: "2024-08-15",
      submissionDate: "2024-08-20",
      dueDate: "2024-09-04",
      currentAmount: 42000,
      retentionAmount: 4200,
      amountDue: 37800,
      status: "Overdue",
      reviewLevel: 3,
      percentComplete: 25,
      description: "Roofing materials and initial installation",
      documentsCount: 3,
      lastUpdated: "2024-08-22"
    },
    {
      id: 5,
      applicationNumber: "PAY-2024-005",
      project: "Mixed-Use Development Epsilon",
      contract: "CNT-2024-005 - Architectural Design Services",
      client: "Urban Spaces Development",
      billingPeriod: "Q3 2024",
      periodStartDate: "2024-07-01",
      periodEndDate: "2024-09-30",
      submissionDate: "2024-09-03",
      dueDate: "2024-09-18",
      currentAmount: 28500,
      retentionAmount: 0,
      amountDue: 28500,
      status: "Pending Review",
      reviewLevel: 1,
      percentComplete: 90,
      description: "Quarterly design services billing",
      documentsCount: 8,
      lastUpdated: "2024-09-03"
    }
  ];

  const filteredApplications = useMemo(() => {
    return applications.filter((app: typeof applications[0]) => {
      const matchesSearch = app.applicationNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          app.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || app.status.toLowerCase().replace(/\s+/g, '') === statusFilter;
      const matchesContract = contractFilter === 'all' || app.contract === contractFilter;

      return matchesSearch && matchesStatus && matchesContract;
    });
  }, [searchTerm, statusFilter, contractFilter, applications]);

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase().replace(' ', '')) {
      case 'pendingreview':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Pending Review</Badge>;
      case 'underreview':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Under Review</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Approved</Badge>;
      case 'paid':
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200">Paid</Badge>;
      case 'overdue':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Overdue</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setContractFilter('all');
  };

  const getTotalStats = () => {
    const pending = applications.filter((app: typeof applications[0]) => app.status === 'Draft' || app.status === 'Pending Review').length;
    const totalAmount = applications.reduce((sum: number, app: typeof applications[0]) => sum + app.amountDue, 0);
    const total = applications.length;
    const overdue = applications.filter((app: typeof applications[0]) => app.status === 'Overdue').length;
    return { pending, totalAmount, total, overdue };
  };

  const stats = getTotalStats();

  const projects = [...new Set(applications.map((app: typeof applications[0]) => app.project).filter(Boolean))];
  const clients = [...new Set(applications.map((app: typeof applications[0]) => app.client).filter(Boolean))];

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Payment Applications (Accounts Receivable)</h2>
          <p className="text-muted-foreground">Submit and track payment applications with comprehensive billing documentation</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <PaymentApplicationFormModal
            mode="add"
            orgId={orgId ?? ''}
            onSuccess={() => refetch()}
            trigger={
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                New Payment Application
              </Button>
            }
          />
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-600" />
              Pending Review
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
            <p className="text-xs text-yellow-600 mt-1">Awaiting approval</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              Total Amount Due
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{formatCurrencyCompact(stats.totalAmount, orgCurrency)}</div>
            <p className="text-xs text-green-600 mt-1">All applications</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              Total Applications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{stats.total}</div>
            <p className="text-xs text-blue-600 mt-1">Applications on record</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              Overdue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{stats.overdue}</div>
            <p className="text-xs text-red-600 mt-1">Past due date</p>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Applications Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Application Directory
          </CardTitle>
          <CardDescription>
            Comprehensive payment application management with G702/G703 documentation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Application Details</TableHead>
                  <TableHead>Project & Client</TableHead>
                  <TableHead>Financial Summary</TableHead>
                  <TableHead>Review Status</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredApplications.map(app => (
                  <TableRow key={app.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="space-y-1">
                        <p className="font-medium">{app.applicationNumber}</p>
                        <p className="text-sm">{app.description}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Badge variant="outline" className="text-xs">{app.billingPeriod}</Badge>
                          <Badge variant="outline" className="text-xs">{app.percentComplete}% Complete</Badge>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <Building className="h-3 w-3" />
                          <span className="font-medium">{app.project}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="h-3 w-3" />
                          <span>{app.client}</span>
                        </div>
                        <div className="text-muted-foreground text-xs">{app.contract}</div>
                        <div className="text-muted-foreground">
                          Period: {app.periodStartDate} - {app.periodEndDate}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div>
                          <span className="font-medium text-lg">${app.amountDue.toLocaleString()}</span>
                          <div className="text-xs text-muted-foreground">
                            Current Work: ${app.currentAmount.toLocaleString()}
                          </div>
                          {app.retentionAmount > 0 && (
                            <div className="text-xs text-muted-foreground">
                              Retention: ${app.retentionAmount.toLocaleString()}
                            </div>
                          )}
                        </div>
                        <div className="text-xs">
                          Progress: {app.percentComplete}%
                        </div>
                        <Progress value={app.percentComplete} className="h-2" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        {getStatusBadge(app.status)}
                        <div className="text-xs text-muted-foreground">
                          Level {app.reviewLevel} of 3
                        </div>
                        <Progress value={(app.reviewLevel / 3) * 100} className="h-2" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          <span>Submitted: {app.submissionDate}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          <span>Due: {app.dueDate}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {app.documentsCount} documents
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <PaymentApplicationFormModal 
                            mode="view" 
                            application={app}
                            trigger={
                              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                            }
                          />
                          <PaymentApplicationFormModal 
                            mode="edit" 
                            application={app}
                            trigger={
                              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Application
                              </DropdownMenuItem>
                            }
                          />
                          <DropdownMenuItem>
                            <Download className="h-4 w-4 mr-2" />
                            Download G702/G703
                          </DropdownMenuItem>
                          {(app as any)._raw && (app as any).status === 'draft' && (
                            <DropdownMenuItem onClick={() => handleSubmitApp((app as any)._raw.application_id)}>
                              <Send className="h-4 w-4 mr-2" />
                              Submit for Review
                            </DropdownMenuItem>
                          )}
                          {(app as any)._raw && (app as any).status === 'submitted' && (
                            <DropdownMenuItem onClick={() => openApproveDialog((app as any)._raw.application_id, (app as any)._raw.net_payment_due)}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Approve
                            </DropdownMenuItem>
                          )}
                          {(app as any)._raw && (
                            <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteApp((app as any)._raw.application_id)}>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {filteredApplications.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No payment applications match your current filters</p>
                <Button variant="outline" size="sm" onClick={resetFilters} className="mt-2">
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Payment Application</DialogTitle>
            <DialogDescription>Enter the approved amount. This may differ from the requested amount (partial approval).</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="approve-amount">Approved Amount ({orgCurrency})</Label>
            <Input
              id="approve-amount"
              type="number"
              min="0"
              step="0.01"
              className="mt-1"
              placeholder="0.00"
              value={approveAmount}
              onChange={(e) => setApproveAmount(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
            <Button onClick={doApproveApp} disabled={!approveAmount || isNaN(parseFloat(approveAmount)) || parseFloat(approveAmount) < 0}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}